import { faClone } from '@fortawesome/free-regular-svg-icons';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { graphql, createFragmentContainer } from 'react-relay';
import {
  Card, CardBody, CardHeader, CardFooter,
  Button, Form, FormFeedback, FormGroup, FormText, Input, Label,
  Col, Row,
} from 'reactstrap';
import styled from 'styled-components';
import isEmail from 'validator/es/lib/isEmail';
import isIn from 'validator/es/lib/isIn';
import isUUID from 'validator/es/lib/isUUID';

import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import { ClauseEdit, ClauseShow } from 'src/components/TimeOfUse';
import FlashesStore from 'src/stores/FlashesStore';
import isActive from 'src/util/isActive';
import {
  TRADE_DIRECTION_UNSPECIFIED, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  Days, DAY_PUBLIC_HOLIDAY, Months,
} from 'src/util/constants';
import { timeOfDayToDuration } from 'src/util/timeOfUse';
import { getTradePointLabel } from '../TradeRuleHelpers';

const AddClauseButton = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start
`;

const MissingClauseFeedback = styled.small`
  display: block;
  margin-top: 0.25rem;
`;

class TradeRuleProposeNominatedTimeOfUseForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Values
      direction: TRADE_DIRECTION_UNSPECIFIED,
      proposerTradePointId: null,
      recipientUserEmail: '',
      clauses: [],
      ignoreDaylightSavings: false,
      ignorePublicHolidays: false,

      // Validation
      directionValid: null,
      proposerTradePointIdValid: null,
      recipientUserEmailValid: null,
      missingClause: false,
      editing: null, // { index }
    };
    this.lastClauseId = 0;
  }

  generateClauseId = () => {
    this.lastClauseId += 1;
    return this.lastClauseId.toString();
  };

  formatClauseFromEdge = (edge) => {
    const { node } = edge;
    const {
      price, monthsOfYear, daysOfWeek, timesOfDay,
    } = node;

    return {
      price,
      monthsOfYear,
      daysOfWeek,
      timesOfDay,
      id: this.generateClauseId(),
    };
  };

  copyFromResidual = () => {
    const { property } = this.props;
    const { meters } = property;

    const {
      direction, proposerTradePointId, directionValid, proposerTradePointIdValid,
    } = this.state;

    if (!directionValid || !proposerTradePointIdValid) {
      return null;
    }

    const meterEdge = meters.edges
      .filter((edge) => edge.node.tradePointId === proposerTradePointId)[0];

    if (meterEdge === undefined) {
      return null;
    }

    const { tradePointId, residualRules } = meterEdge.node;

    let clauses = [];
    const currentRules = residualRules.edges.filter((edge) => {
      if (direction === TRADE_DIRECTION_BUY) {
        return edge.node.buyer.tradePoint.id === tradePointId;
      }
      if (direction === TRADE_DIRECTION_SELL) {
        return edge.node.seller.tradePoint.id === tradePointId;
      }
      return false;
    });
    if (currentRules.length === 1) {
      clauses = currentRules[0].node.clauses.edges.map(this.formatClauseFromEdge);
    }

    if (clauses.length === 0) {
      return null;
    }

    let ignorePublicHolidays = false;
    if (residualRules.edges.length > 0) {
      const rule = residualRules.edges[0].node;
      if (rule.clauses.edges.length > 0) {
        const clause = rule.clauses.edges[0].node;
        if (clause.ignorePublicHolidays) {
          ignorePublicHolidays = true;
        }
      }
    }

    return { clauses, ignorePublicHolidays };
  };

  validateDirection = () => {
    const { direction } = this.state;
    const directionValid = isIn(direction, [TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL]);

    this.setState({ directionValid });
  };

  validateProposerTradePointId = () => {
    const { proposerTradePointId } = this.state;
    const proposerTradePointIdValid = proposerTradePointId ? isUUID(proposerTradePointId) : false;
    this.setState({ proposerTradePointIdValid });
  };

  validateRecipientUserEmail = () => {
    const { recipientUserEmail } = this.state;
    const recipientUserEmailValid = isEmail(recipientUserEmail);
    this.setState({ recipientUserEmailValid });
  };

  validateClauses = () => {
    const { clauses } = this.state;
    if (clauses.length === 0) {
      this.setState({ missingClause: true });
      return;
    }
    this.setState({ missingClause: false });
  };

  validateForm = () => {
    this.validateDirection();
    this.validateProposerTradePointId();
    this.validateRecipientUserEmail();
    this.validateClauses();
  };

  handleSubmit = (event) => {
    event.preventDefault();

    const {
      handleTradeRuleProposeNominated, processing, property, intl,
    } = this.props;
    const { timezone, publicHolidayRegion } = property;

    const mutationName = 'proposeNominatedTrade';

    if (processing) {
      const processingMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated_time_of_use.response_message.processing', defaultMessage: 'We are still processing your request...' });
      FlashesStore.flash(FlashesStore.INFO, processingMsg, mutationName);
      return;
    }

    this.validateForm();

    if (!this.isValid()) {
      const errorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated_time_of_use.response_message.error', defaultMessage: 'Form data not valid. Please see below.' });
      FlashesStore.flash(FlashesStore.ERROR, errorMsg, mutationName);
      return;
    }

    const {
      direction, proposerTradePointId, recipientUserEmail, clauses,
      ignoreDaylightSavings, ignorePublicHolidays,
    } = this.state;

    const formatClause = (clause) => {
      const {
        price, monthsOfYear, daysOfWeek, timesOfDay,
      } = clause;

      return {
        price,
        timezone,
        publicHolidayRegion,
        ignoreDaylightSavings,
        ignorePublicHolidays,
        monthsOfYear,
        daysOfWeek,
        timesOfDay,
      };
    };

    const input = {
      direction,
      clauses: clauses.map(formatClause),
      proposerTradePointId,
      recipientUserEmail,
    };

    handleTradeRuleProposeNominated(input);
  };

  handleDirectionChange = (event) => {
    const { value: direction } = event.target;
    const directionValid = isIn(direction, [TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL]);

    this.setState({ direction, directionValid });
  };

  handleRecipientUserEmailChange = (event) => {
    const { value: recipientUserEmail } = event.target;
    const recipientUserEmailValid = isEmail(recipientUserEmail);

    this.setState({ recipientUserEmail, recipientUserEmailValid });
  };

  handleProposerTradePointIdChange = (event) => {
    const { value: proposerTradePointId } = event.target;
    const proposerTradePointIdValid = isUUID(proposerTradePointId);

    this.setState({ proposerTradePointId, proposerTradePointIdValid });
  };

  handleIgnoreDaylightSavingsChange = (event) => {
    const { checked: ignoreDaylightSavings } = event.target;

    this.setState({ ignoreDaylightSavings });
  };

  handleIgnorePublicHolidaysChange = (event) => {
    const { checked: ignorePublicHolidays } = event.target;

    this.setState({ ignorePublicHolidays });
  };

  isEditing = (index) => {
    const { editing } = this.state;

    if (index === undefined) {
      return !!editing;
    }

    return editing && editing.index === index;
  };

  editClause = (index) => {
    this.setState({ editing: { index } });
  };

  addClause = () => {
    this.setState({ editing: { index: null } });
  };

  removeClause = (index) => {
    const { clauses } = this.state;

    const newClauses = [...clauses];
    newClauses.splice(index, 1);
    this.setState({ clauses: newClauses });
  };

  editClauseSet = (index, newClause) => {
    const { clauses } = this.state;

    const newClauses = [...clauses];
    newClauses[index] = { ...newClause, id: clauses[index].id };
    this.setState({ clauses: newClauses, editing: null });
  };

  addClauseSet = (newClause) => {
    const { clauses } = this.state;

    const newClauses = [...clauses];
    newClauses.push({ ...newClause, id: this.generateClauseId() });
    this.setState({ clauses: newClauses, editing: null, missingClause: false });
  };

  cancelEdit = () => {
    this.setState({ editing: null });
  };

  isValid = () => {
    const {
      clauses,
      directionValid,
      proposerTradePointIdValid,
      recipientUserEmailValid,
    } = this.state;

    return directionValid !== null && directionValid
      && proposerTradePointIdValid !== null && proposerTradePointIdValid
      && recipientUserEmailValid !== null && recipientUserEmailValid
      && this.validClauses(clauses);
  };

  validClauses = (clauses) => {
    const { ignorePublicHolidays } = this.state;

    if (clauses.length === 0) {
      return false;
    }

    const relevantDays = ignorePublicHolidays
      ? Days.filter((day) => day !== DAY_PUBLIC_HOLIDAY) : Days;

    const combinations = [];
    clauses.forEach(({ monthsOfYear, daysOfWeek, timesOfDay }) => {
      monthsOfYear.forEach((month) => {
        daysOfWeek.forEach((day) => {
          if (ignorePublicHolidays && day === DAY_PUBLIC_HOLIDAY) {
            return;
          }
          timesOfDay.forEach(({ start, finish }) => {
            combinations.push({
              month: Months.indexOf(month),
              day: relevantDays.indexOf(day),
              time: {
                start: timeOfDayToDuration(start).valueOf(),
                finish: timeOfDayToDuration(finish).valueOf(),
              },
            });
          });
        });
      });
    });

    combinations.sort((a, b) => (a.month - b.month || a.day - b.day
      || a.time.start - b.time.start || a.time.finish - b.time.finish));

    for (let i = 1; i < combinations.length; i += 1) {
      const curr = combinations[i];
      const prev = combinations[i - 1];

      const comparison = curr.month - prev.month || curr.day - prev.day
        || curr.time.start - prev.time.finish;

      if (comparison < 0) {
        return false;
      }
    }

    return true;
  };

  render() {
    const {
      intl, property, processing, router,
    } = this.props;
    const { meters } = property;
    const meterNodes = meters && meters.edges.map((edge) => (edge.node));

    const {
      direction, proposerTradePointId, recipientUserEmail, clauses,
      ignoreDaylightSavings, ignorePublicHolidays, missingClause,
      directionValid, proposerTradePointIdValid, recipientUserEmailValid,
    } = this.state;

    const optionTradePointDefault = intl.formatMessage({
      id: 'trade_rule_proposal.select_tradepoint_label',
      defaultMessage: 'Select a Trade Point',
    });

    const optionDirectionBuy = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated_time_of_use.direction.trade_direction_buy.label', defaultMessage: 'Buy' });
    const optionDirectionSell = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated_time_of_use.direction.trade_direction_sell.label', defaultMessage: 'Sell' });
    const optionDirectionDefault = intl.formatMessage(
      { id: 'trade_rule.trade_rule_propose_nominated_time_of_use.option.direction.default_option.label', defaultMessage: 'Select "{optionDirectionBuy}" or "{optionDirectionSell}"' },
      { optionDirectionBuy, optionDirectionSell },
    );
    const tradePointLabel = getTradePointLabel(intl, direction);

    return (
      <Form onSubmit={this.handleSubmit} disabled={processing}>
        <Card>
          <CardHeader className="d-flex flex-wrap">
            <h2 className="mb-0">
              <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.propose_nominated_trade.label" defaultMessage="Propose a peer-to-peer trade" />
            </h2>
            <Link to={`/properties/${property.id}/trade-rules/propose/nominated`} className="btn btn-darken ms-auto">
              <FormattedMessage id="trade_rule_flat_pricing.label" defaultMessage=" Flat pricing" />
            </Link>
          </CardHeader>
          <CardBody>
            <FormGroup>
              <Label for="direction"><FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.direction.label" defaultMessage="Are you buying or selling?" /></Label>
              <Input type="select" name="direction" id="direction" defaultValue={direction} onChange={this.handleDirectionChange} disabled={processing} valid={directionValid} invalid={directionValid !== null && !directionValid}>
                <option value={null}>{optionDirectionDefault}</option>
                <option value={TRADE_DIRECTION_BUY}>{optionDirectionBuy}</option>
                <option value={TRADE_DIRECTION_SELL}>{optionDirectionSell}</option>
              </Input>
              <FormFeedback><FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.direction.invalid_direction.feedback" defaultMessage="Invalid direction" /></FormFeedback>
              <FormText>
                <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.direction.hint" defaultMessage="Are you proposing to sell your energy, or buy energy from a friend?" />
              </FormText>
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="proposerTradePointId">{tradePointLabel}</Label>
                  <Input type="select" name="select" id="proposerTradePointId" defaultValue={proposerTradePointId} onChange={this.handleProposerTradePointIdChange} disabled={processing} valid={proposerTradePointIdValid} invalid={proposerTradePointIdValid !== null && !proposerTradePointIdValid}>
                    <option key="meter-id-null">
                      {optionTradePointDefault}
                    </option>
                    {meterNodes && meterNodes.map((meter) => (
                      <option
                        value={meter.tradePointId}
                        disabled={!isActive(meter.active)}
                        key={meter.id}
                      >
                        {`${meter.title} (${meter.identifier})`}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback><FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.trade_point.invalid_trade_point.feedback" defaultMessage="Invalid trade point selection" /></FormFeedback>
                  <FormText>
                    <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.tradepoint.hint" defaultMessage="Which trade point are you using?" />
                  </FormText>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="recipientUserEmail"><FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.label" defaultMessage="Recipient email" /></Label>
                  <Input type="email" name="recipientUserEmail" id="recipientUserEmail" value={recipientUserEmail} onChange={this.handleRecipientUserEmailChange} disabled={processing} valid={recipientUserEmailValid} invalid={recipientUserEmailValid !== null && !recipientUserEmailValid} />
                  <FormFeedback><FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.invalid_email.feedback" defaultMessage="Invalid email" /></FormFeedback>
                  <FormText>
                    {/* FormattedMessage required (not intl.formatMessage) for JSX component interpolation. See: https://formatjs.github.io/docs/react-intl/components/#rich-text-formatting */}
                    {/* intl.formatMessage() returns strings only, renders React components as [object Object]. See: https://formatjs.github.io/docs/react-intl/api/#usage */}
                    <FormattedMessage
                      id="trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.hint.text"
                      defaultMessage="Who do you want to propose to trade with?"
                      values={{
                        link: (chunks) => (
                          <LinkInI18nBlock
                            url={intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.hint.url', defaultMessage: 'trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.hint.url' })}
                            urlKey="trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.hint.url"
                            chunks={chunks}
                          />
                        ),
                      }}
                    />
                  </FormText>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              {clauses.map((clause, index) => {
                if (this.isEditing(index)) {
                  return (
                    <ClauseEdit
                      clause={clause}
                      setClause={
                        (newClause) => this.editClauseSet(index, newClause)
                      }
                      cancelEdit={this.cancelEdit}
                      priceHelpText={<FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.price.hint" defaultMessage="What price, in c/kWh, do you want to propose?" />}
                      priceLabel={<FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.price.label" defaultMessage="Price" />}
                      key={clause.id}
                    />
                  );
                }
                return (
                  <ClauseShow
                    clause={clause}
                    showControls={!this.isEditing()}
                    editClause={() => this.editClause(index)}
                    removeClause={() => this.removeClause(index)}
                    key={clause.id}
                  />
                );
              })}
              {this.isEditing(null) && (
                <ClauseEdit
                  setClause={(newClause) => this.addClauseSet(newClause)}
                  cancelEdit={this.cancelEdit}
                  priceHelpText={<FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.price.hint" defaultMessage="What price, in c/kWh, do you want to propose?" />}
                  priceLabel={<FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.price.label" defaultMessage="Price" />}
                />
              )}
              {!this.isEditing() && (
                <AddClauseButton>
                  <Button color="darken" onClick={() => this.addClause()}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.add_clause.label" defaultMessage="Add clause" />
                  </Button>

                  {missingClause && (
                  <MissingClauseFeedback className="text-danger">
                    <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.missing_clause.feedback" defaultMessage="You must add at least one clause" />
                  </MissingClauseFeedback>
                  )}
                </AddClauseButton>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="ignoreDaylightSavings">
                <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.ignore_day_light_savings.label" defaultMessage="Ignore daylight savings" />
                <Input
                  type="checkbox"
                  checked={ignoreDaylightSavings}
                  className="mb-2"
                  disabled={processing}
                  id="ignoreDaylightSavings"
                  label="Ignore daylight savings"
                  name="ignoreDaylightSavings"
                  onChange={this.handleIgnoreDaylightSavingsChange}
                  value={`${ignoreDaylightSavings}`}
                />
              </Label>
              <Label for="ignorePublicHolidays">
                <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.ignore_public_holidays.label" defaultMessage="Ignore public holidays" />
                <Input
                  type="checkbox"
                  checked={ignorePublicHolidays}
                  className="mb-2"
                  disabled={processing}
                  id="ignorePublicHolidays"
                  label="Ignore public holidays"
                  name="ignorePublicHolidays"
                  onChange={this.handleIgnorePublicHolidaysChange}
                  value={`${ignorePublicHolidays}`}
                />
              </Label>
            </FormGroup>
          </CardBody>
          <CardFooter className="d-flex">
            <Button disabled={processing || this.isEditing()}>
              <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.submit.label" defaultMessage="Propose" />
            </Button>
            <Button color="" onClick={() => (router.go(-1))} disabled={processing || this.isEditing()}>
              <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.cancel.label" defaultMessage="Cancel" />
            </Button>
            <Button
              color="darken"
              className="ms-auto"
              onClick={() => this.setState(this.copyFromResidual())}
              disabled={processing || this.isEditing() || !this.copyFromResidual()}
            >
              <FontAwesomeIcon icon={faClone} className="me-2" />
              <FormattedMessage id="trade_rule.trade_rule_propose_nominated_time_of_use.price.copy_from_tariff.label" defaultMessage="Copy from retailer default" />
            </Button>
          </CardFooter>
        </Card>
      </Form>
    );
  }
}

TradeRuleProposeNominatedTimeOfUseForm.propTypes = {
  handleTradeRuleProposeNominated: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  processing: PropTypes.bool,
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

TradeRuleProposeNominatedTimeOfUseForm.defaultProps = {
  handleTradeRuleProposeNominated: null,
  property: null,
  processing: false,
};

export default createFragmentContainer(
  injectIntl(TradeRuleProposeNominatedTimeOfUseForm),
  {
    property: graphql`
      fragment TradeRuleProposeNominatedTimeOfUseForm_property on Property {
        id
        title
        timezone
        publicHolidayRegion
        meters {
          edges {
            node {
              id
              identifier
              title
              tradePointId
              active { start finish }
              residualRules: rules(first: 500, type: TRADE_TYPE_RESIDUAL, state: TRADE_RULE_STATE_ACCEPTED, start: $now, finish: $now) {
                edges {
                  node {
                    id
                    priority
                    tradeType
                    state
                    buyer {
                      userId
                      communityId
                      residualId
                      tradePoint {
                        id
                      }
                    }
                    seller {
                      userId
                      communityId
                      residualId
                      tradePoint {
                        id
                      }
                    }
                    clauses {
                      edges {
                        node {
                          price
                          ignorePublicHolidays
                          monthsOfYear
                          daysOfWeek
                          timesOfDay {
                            start  { hours minutes seconds }
                            finish { hours minutes seconds }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
  },
);
